import { useFormik } from "formik";
import { useCallback, useMemo, useState } from "react";
import * as Yup from "yup";
import {
  NOTE_PREVIEW_COSIGN_DEFAULT_VALUES,
  NOTE_PREVIEW_LABELS,
} from "../note-preview-constant";
import { CosignFormValues } from "../note-preview-types";

const COSIGN_VALIDATION_SCHEMA = Yup.object({
  cosignerNote: Yup.string().trim().label(NOTE_PREVIEW_LABELS.cosignerNote),
});

interface Props {
  onSubmit: (values: CosignFormValues) => void | Promise<void>;
}

export interface UseCosignFormResult {
  note: string;
  noteError?: string;
  isEditingNote: boolean;
  openNoteEditor: () => void;
  onNoteChange: (value: string) => void;
  onNoteBlur: () => void;
  submit: () => void;
}

/** Optional co-signer note; the PIN and signature are collected by the dialogs that follow. */
export const useCosignForm = ({ onSubmit }: Props): UseCosignFormResult => {
  const [isEditingNote, setIsEditingNote] = useState(false);

  const formik = useFormik<CosignFormValues>({
    initialValues: { ...NOTE_PREVIEW_COSIGN_DEFAULT_VALUES },
    validationSchema: COSIGN_VALIDATION_SCHEMA,
    onSubmit: async (values) => {
      await onSubmit({ cosignerNote: values.cosignerNote.trim() });
    },
  });

  const openNoteEditor = useCallback(() => setIsEditingNote(true), []);
  const onNoteChange = useCallback(
    (value: string) => formik.setFieldValue("cosignerNote", value),
    [formik]
  );
  const onNoteBlur = useCallback(
    () => formik.setFieldTouched("cosignerNote", true),
    [formik]
  );
  const submit = useCallback(() => {
    formik.submitForm();
  }, [formik]);

  return useMemo(
    () => ({
      note: formik.values.cosignerNote,
      noteError: formik.touched.cosignerNote
        ? formik.errors.cosignerNote
        : undefined,
      isEditingNote,
      openNoteEditor,
      onNoteChange,
      onNoteBlur,
      submit,
    }),
    [
      formik.values.cosignerNote,
      formik.touched.cosignerNote,
      formik.errors.cosignerNote,
      isEditingNote,
      openNoteEditor,
      onNoteChange,
      onNoteBlur,
      submit,
    ]
  );
};
